import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Checkbox } from '../components/ui/Checkbox';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { ArrowLeftIcon, EditIcon, MailIcon, PhoneIcon, MapPinIcon, BuildingIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ClockIcon, XCircleIcon, CreditCardIcon, CalendarIcon, RepeatIcon, ChevronDownIcon } from 'lucide-react';
// Types
interface CustomerDetails {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  terms_accepted: number | string | boolean;
  invoices_number?: number | string;
  subscriptions_number?: number | string;
}
interface CustomerInvoice {
  id: string;
  number: string;
  date: number;
  amount: number;
  status: string;
  source?: string;
}
interface CustomerSubscription {
  id: string;
  number: string;
  name: string;
  status: string;
  cycle: string;
  cycle_num: number;
  amount: number;
  next_invoice_date: number;
}
export function CustomerView() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  // Check if current company is TKDscore
  const isTKDscore = selectedCompany?.name?.toLowerCase().includes('tkdscore') || false;
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Invoices state
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [totalInvoices, setTotalInvoices] = useState(0);
  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<CustomerSubscription[]>([]);
  const [subscriptionsPage, setSubscriptionsPage] = useState(1);
  const [subscriptionsHasMore, setSubscriptionsHasMore] = useState(false);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    terms_accepted: false,
    wholesale_customer: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const convertTermsAccepted = (value: number | string | boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value === 'true';
    return false;
  };
  const fetchInvoices = async (page: number, append: boolean = false) => {
    if (!id || !selectedCompanyId) return;
    setIsLoadingInvoices(true);
    try {
      const response = await callBillingAPI('getInvoices', {
        company_id: selectedCompanyId,
        customer_id: id,
        page,
        per_page_limit: 5
      });
      const mapped = (response.items || []).map((inv: any) => ({
        id: inv.id,
        number: inv.number,
        date: inv.invoice_date || inv.created,
        amount: parseFloat(inv.amount) || 0,
        status: inv.status,
        source: inv.source || ''
      }));
      setInvoices(prev => append ? [...prev, ...mapped] : mapped);
      setInvoicesHasMore(response.has_next_page || false);
      setInvoicesPage(page);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };
  const fetchSubscriptions = async (page: number, append: boolean = false) => {
    if (!id || !selectedCompanyId) return;
    setIsLoadingSubscriptions(true);
    try {
      const response = await callBillingAPI('getSubscriptions', {
        company_id: selectedCompanyId,
        customer_id: id,
        page,
        per_page_limit: 5
      });
      const mapped = (response.items || []).map((sub: any) => ({
        id: sub.id,
        number: sub.number,
        name: sub.name || '',
        status: sub.status,
        cycle: sub.cycle,
        cycle_num: parseInt(String(sub.cycle_num)) || 1,
        amount: parseFloat(String(sub.amount)) || 0,
        next_invoice_date: sub.next_invoice_date
      }));
      setSubscriptions(prev => append ? [...prev, ...mapped] : mapped);
      setSubscriptionsHasMore(response.has_next_page || false);
      setSubscriptionsPage(page);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };
  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const customerData = await callBillingAPI('getCustomer', {
        id
      });
      setCustomer(customerData);
      setFormData({
        first_name: customerData.first_name || '',
        last_name: customerData.last_name || '',
        email: customerData.email || '',
        phone: customerData.phone || '',
        organization: customerData.organization || '',
        address_street: customerData.address_street || '',
        address_city: customerData.address_city || '',
        address_state: customerData.address_state || '',
        address_zip: customerData.address_zip || '',
        terms_accepted: convertTermsAccepted(customerData.terms_accepted),
        wholesale_customer: !!(customerData as any).wholesale_customer
      });
      const invCount = parseInt(String(customerData.invoices_number)) || 0;
      const subCount = parseInt(String(customerData.subscriptions_number)) || 0;
      setTotalInvoices(invCount);
      setTotalSubscriptions(subCount);
      // Only fetch if there are items
      if (invCount > 0) {
        fetchInvoices(1);
      } else {
        setInvoices([]);
        setInvoicesHasMore(false);
      }
      if (subCount > 0) {
        fetchSubscriptions(1);
      } else {
        setSubscriptions([]);
        setSubscriptionsHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load customer data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customer data');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [id, selectedCompanyId]);
  const handleSave = async () => {
    const errors: Record<string, string> = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      await callBillingAPI('editCustomer', {
        id,
        ...formData,
        terms_accepted: formData.terms_accepted ? 1 : 0,
        wholesale_customer: formData.wholesale_customer ? 1 : 0
      });
      setIsEditModalOpen(false);
      fetchData();
    } catch (err) {
      setFormErrors({
        submit: err instanceof Error ? err.message : 'Failed to save'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, any> = {
      paid: {
        variant: 'success',
        label: 'Paid',
        icon: CheckCircleIcon
      },
      sent: {
        variant: 'info',
        label: 'Sent',
        icon: ClockIcon
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue',
        icon: AlertCircleIcon
      },
      draft: {
        variant: 'default',
        label: 'Draft',
        icon: FileTextIcon
      },
      voided: {
        variant: 'default',
        label: 'Voided',
        icon: XCircleIcon
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial',
        icon: CreditCardIcon
      }
    };
    const config = styles[status] || styles.draft;
    const Icon = config.icon;
    return <Badge variant={config.variant} className="flex items-center gap-1" data-id="element-1021">
        <Icon className="w-3 h-3" data-id="element-1022" />
        {config.label}
      </Badge>;
  };
  const getSubStatusBadge = (status: string) => {
    const styles: Record<string, any> = {
      live: {
        variant: 'success',
        label: 'Live'
      },
      paused: {
        variant: 'warning',
        label: 'Paused'
      },
      cancelled: {
        variant: 'danger',
        label: 'Cancelled'
      },
      completed: {
        variant: 'default',
        label: 'Completed'
      }
    };
    const config = styles[status] || {
      variant: 'default',
      label: status
    };
    return <Badge variant={config.variant} data-id="element-1023">{config.label}</Badge>;
  };
  const getCycleName = (cycle: string, cycleNum: number = 1): string => {
    const names: Record<string, {
      singular: string;
      plural: string;
    }> = {
      week: {
        singular: 'Week',
        plural: 'Weeks'
      },
      month: {
        singular: 'Month',
        plural: 'Months'
      },
      year: {
        singular: 'Year',
        plural: 'Years'
      }
    };
    const n = names[cycle] || {
      singular: cycle,
      plural: cycle
    };
    return cycleNum === 1 ? `Every ${n.singular}` : `Every ${cycleNum} ${n.plural}`;
  };
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-1024">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1025"></div>
      </div>;
  }
  if (error || !customer) {
    return <div className="p-8 text-center" data-id="element-1026">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1027">
          <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1028" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-1029">
          Customer Not Found
        </h2>
        <p className="text-gray-500 mt-2" data-id="element-1030">
          {error || 'The requested customer could not be found.'}
        </p>
        <Button className="mt-4" onClick={() => navigate('/customers')} data-id="element-1031">
          Back to Customers
        </Button>
      </div>;
  }
  const fullAddress = [customer.address_street, customer.address_city, customer.address_state, customer.address_zip].filter(Boolean).join(', ');
  return <div className="space-y-6" data-id="element-1032">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1033">
        <div className="flex items-center gap-4" data-id="element-1034">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} data-id="element-1035">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1036" />
            Back
          </Button>
          <div data-id="element-1037">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1038">
              {customer.first_name} {customer.last_name}
            </h1>
            {customer.organization && <div className="flex items-center gap-2 text-gray-500 mt-1" data-id="element-1039">
                <BuildingIcon className="w-4 h-4" data-id="element-1040" />
                <span data-id="element-1041">{customer.organization}</span>
              </div>}
          </div>
        </div>
        <Button onClick={() => setIsEditModalOpen(true)} leftIcon={<EditIcon className="w-4 h-4" data-id="element-1043" />} data-id="element-1042">
          Edit Customer
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1044">
        {/* Left Column: Details */}
        <div className="space-y-6" data-id="element-1045">
          <Card data-id="element-1046">
            <CardHeader title="Contact Information" data-id="element-1047" />
            <div className="space-y-4" data-id="element-1048">
              <div className="flex items-start gap-3" data-id="element-1049">
                <MailIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1050" />
                <div data-id="element-1051">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1052">Email</p>
                  <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline" data-id="element-1053">
                    {customer.email}
                  </a>
                </div>
              </div>

              {customer.phone && <div className="flex items-start gap-3" data-id="element-1054">
                  <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1055" />
                  <div data-id="element-1056">
                    <p className="text-sm font-medium text-gray-500" data-id="element-1057">Phone</p>
                    <a href={`tel:${customer.phone}`} className="text-gray-900 hover:text-gray-700" data-id="element-1058">
                      {customer.phone}
                    </a>
                  </div>
                </div>}

              <div className="flex items-start gap-3" data-id="element-1059">
                <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1060" />
                <div data-id="element-1061">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1062">Address</p>
                  <p className="text-gray-900" data-id="element-1063">
                    {fullAddress || <span className="text-gray-400 italic" data-id="element-1064">
                        No address provided
                      </span>}
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100" data-id="element-1065">
                <div className="flex items-center justify-between" data-id="element-1066">
                  <span className="text-sm text-gray-500" data-id="element-1067">Terms Accepted</span>
                  <Badge variant={convertTermsAccepted(customer.terms_accepted) ? 'success' : 'warning'} data-id="element-1068">
                    {convertTermsAccepted(customer.terms_accepted) ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>

              {isTKDscore && <div className="pt-4 border-t border-gray-100" data-id="element-1069">
                  <div className="flex items-center justify-between" data-id="element-1070">
                    <span className="text-sm text-gray-500" data-id="element-1071">
                      Wholesale Customer
                    </span>
                    <Badge variant={(customer as any).wholesale_customer ? 'success' : 'default'} data-id="element-1072">
                      {(customer as any).wholesale_customer ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>}
            </div>
          </Card>
        </div>

        {/* Right Column: Invoices & Subscriptions */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1073">
          {/* Invoices */}
          <Card data-id="element-1074">
            <CardHeader title={`Invoices${totalInvoices > 0 ? ` (${totalInvoices})` : ''}`} action={<Button size="sm" variant="secondary" onClick={() => navigate('/invoices/new', {
            state: {
              customerId: id,
              customerName: `${customer.first_name} ${customer.last_name}`.trim()
            }
          })} data-id="element-1076">
                  Create Invoice
                </Button>} data-id="element-1075" />
            {totalInvoices === 0 ? <div className="py-8 text-center text-gray-500 text-sm" data-id="element-1077">
                No invoices found for this customer
              </div> : <>
                <Table columns={[{
              key: 'number',
              header: 'Number',
              render: inv => <div data-id="element-1079">
                          <span className="font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/invoices/${inv.id}`)} data-id="element-1080">
                            {inv.number}
                          </span>
                          {inv.source && <p className="text-xs text-gray-400 mt-0.5" data-id="element-1081">
                              {inv.source}
                            </p>}
                        </div>
            }, {
              key: 'date',
              header: 'Date',
              render: inv => <div className="flex items-center gap-2 text-gray-600" data-id="element-1082">
                          <CalendarIcon className="w-3 h-3" data-id="element-1083" />
                          {new Date(inv.date * 1000).toLocaleDateString()}
                        </div>
            }, {
              key: 'amount',
              header: 'Amount',
              render: inv => formatCurrency(inv.amount)
            }, {
              key: 'status',
              header: 'Status',
              render: inv => getStatusBadge(inv.status)
            }]} data={invoices} keyExtractor={inv => inv.id} emptyMessage="Loading..." data-id="element-1078" />
                {invoicesHasMore && <div className="p-4 border-t border-gray-200 text-center" data-id="element-1084">
                    <Button variant="ghost" size="sm" onClick={() => fetchInvoices(invoicesPage + 1, true)} isLoading={isLoadingInvoices} leftIcon={<ChevronDownIcon className="w-4 h-4" data-id="element-1086" />} data-id="element-1085">
                      Show More Invoices
                    </Button>
                  </div>}
              </>}
          </Card>

          {/* Subscriptions */}
          <Card data-id="element-1087">
            <CardHeader title={`Subscriptions${totalSubscriptions > 0 ? ` (${totalSubscriptions})` : ''}`} action={<Button size="sm" variant="secondary" onClick={() => navigate('/subscriptions/new', {
            state: {
              customerId: id,
              customerName: `${customer.first_name} ${customer.last_name}`.trim()
            }
          })} data-id="element-1089">
                  Create Subscription
                </Button>} data-id="element-1088" />
            {totalSubscriptions === 0 ? <div className="py-8 text-center text-gray-500 text-sm" data-id="element-1090">
                No subscriptions found for this customer
              </div> : <>
                <Table columns={[{
              key: 'number',
              header: 'Number',
              render: sub => <span className="font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/subscriptions/${sub.id}`)} data-id="element-1092">
                          {sub.number}
                        </span>
            }, {
              key: 'name',
              header: 'Name',
              render: sub => <span className="text-gray-900" data-id="element-1093">{sub.name || '—'}</span>
            }, {
              key: 'cycle',
              header: 'Cycle',
              render: sub => <span className="text-gray-600 text-sm" data-id="element-1094">
                          {getCycleName(sub.cycle, sub.cycle_num)}
                        </span>
            }, {
              key: 'amount',
              header: 'Amount',
              render: sub => formatCurrency(sub.amount)
            }, {
              key: 'status',
              header: 'Status',
              render: sub => getSubStatusBadge(sub.status)
            }]} data={subscriptions} keyExtractor={sub => sub.id} emptyMessage="Loading..." data-id="element-1091" />
                {subscriptionsHasMore && <div className="p-4 border-t border-gray-200 text-center" data-id="element-1095">
                    <Button variant="ghost" size="sm" onClick={() => fetchSubscriptions(subscriptionsPage + 1, true)} isLoading={isLoadingSubscriptions} leftIcon={<ChevronDownIcon className="w-4 h-4" data-id="element-1097" />} data-id="element-1096">
                      Show More Subscriptions
                    </Button>
                  </div>}
              </>}
          </Card>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Customer" size="lg" data-id="element-1098">
        <div className="space-y-4" data-id="element-1099">
          <div className="grid grid-cols-2 gap-4" data-id="element-1100">
            <Input label="First Name *" value={formData.first_name} onChange={e => setFormData({
            ...formData,
            first_name: e.target.value
          })} error={formErrors.first_name} data-id="element-1101" />
            <Input label="Last Name" value={formData.last_name} onChange={e => setFormData({
            ...formData,
            last_name: e.target.value
          })} data-id="element-1102" />
          </div>

          <Input label="Email *" value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} error={formErrors.email} data-id="element-1103" />

          <Input label="Phone" value={formData.phone} onChange={e => setFormData({
          ...formData,
          phone: e.target.value
        })} data-id="element-1104" />

          <Input label="Organization" value={formData.organization} onChange={e => setFormData({
          ...formData,
          organization: e.target.value
        })} data-id="element-1105" />

          <Input label="Street Address" value={formData.address_street} onChange={e => setFormData({
          ...formData,
          address_street: e.target.value
        })} data-id="element-1106" />

          <div className="grid grid-cols-3 gap-4" data-id="element-1107">
            <Input label="City" value={formData.address_city} onChange={e => setFormData({
            ...formData,
            address_city: e.target.value
          })} data-id="element-1108" />
            <Input label="State" value={formData.address_state} onChange={e => setFormData({
            ...formData,
            address_state: e.target.value
          })} data-id="element-1109" />
            <Input label="ZIP" value={formData.address_zip} onChange={e => setFormData({
            ...formData,
            address_zip: e.target.value
          })} data-id="element-1110" />
          </div>

          <Checkbox label="Terms Accepted" checked={formData.terms_accepted} onChange={e => setFormData({
          ...formData,
          terms_accepted: e.target.checked
        })} data-id="element-1111" />

          {isTKDscore && <Checkbox label="Wholesale Customer" checked={formData.wholesale_customer} onChange={e => setFormData({
          ...formData,
          wholesale_customer: e.target.checked
        })} data-id="element-1112" />}

          {formErrors.submit && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg" data-id="element-1113">
              {formErrors.submit}
            </div>}

          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-1114">
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} data-id="element-1115">
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isSaving} data-id="element-1116">
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}